import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Bot, Brain, Landmark, MessageCircle, Network,
  Plug, ShoppingBag, Database
} from 'lucide-react';

/**
 * Public integrations directory — every LLM, platform, agent surface, and
 * communication tool Commerce Index connects to, with honest status labels.
 * Each category is also an SEO surface ("<tool> + AI commerce agents").
 */

const STATUS_STYLES = {
  Live: 'bg-[#3EF1C7]/10 text-[#3EF1C7] border-[#3EF1C7]/30',
  Beta: 'bg-[#FFD166]/10 text-[#FFD166] border-[#FFD166]/30',
  Roadmap: 'bg-[#64748B]/10 text-[#94A3B8] border-[#64748B]/30',
};

const CATEGORIES = [
  {
    id: 'commerce',
    icon: ShoppingBag,
    title: 'Commerce Platforms',
    blurb: 'Connect your store — agents scan products, inventory, orders, and pricing the same night.',
    items: [
      { name: 'Shopify', status: 'Live', desc: 'OAuth install, embedded app, product/inventory/order sync, compliance webhooks.', link: '/docs/integrations' },
      { name: 'Amazon (Seller & Vendor)', status: 'Beta', desc: 'Marketplace listings, orders, and buy-box intelligence.', link: '/docs/integrations' },
      { name: 'Walmart Marketplace', status: 'Beta', desc: 'Listings and order flow for Walmart sellers.', link: '/docs/integrations' },
      { name: 'eBay', status: 'Beta', desc: 'Listing sync and deal distribution.', link: '/docs/integrations' },
      { name: 'Etsy', status: 'Beta', desc: 'Shop sync for makers and craft sellers.', link: '/docs/integrations' },
      { name: 'BigCommerce', status: 'Beta', desc: 'Catalog and order integration.', link: '/docs/integrations' },
      { name: 'WooCommerce', status: 'Roadmap', desc: 'Storefront sync for WordPress commerce.' },
    ],
  },
  {
    id: 'llms',
    icon: Brain,
    title: 'LLMs — Bring Your Own Model',
    blurb: 'Run your agents on our keys or yours. Any OpenAI-compatible or local endpoint works.',
    items: [
      { name: 'Anthropic Claude', status: 'Live', desc: 'Default reasoning engine. Bring your own key in chat settings — validated before use.', link: '/docs' },
      { name: 'OpenAI-compatible endpoints', status: 'Live', desc: 'Point any agent at your own endpoint via per-merchant base URL + key.' },
      { name: 'Ollama / local models', status: 'Live', desc: 'Self-hosted models (Llama, Hermes, Mistral) on any reachable server.' },
      { name: 'Kimi / Moonshot', status: 'Live', desc: 'Preset provider in the LLM client.' },
      { name: 'Paperclip (Hermes gateway)', status: 'Beta', desc: 'Mirror your portfolio into paperclip.ing; unified agent cost tracking and approvals.' },
    ],
  },
  {
    id: 'agents',
    icon: Bot,
    title: 'Agents & MCP',
    blurb: 'The commerce OS surface for other AIs — every call metered, every action auditable.',
    items: [
      { name: 'MCP Server', status: 'Live', desc: '20+ commerce tools any MCP client (Claude, IDEs, agent platforms) can call.', link: '/developers' },
      { name: 'Agent Bridge (commerceindex.ai)', status: 'Live', desc: 'Cross-domain USDC settlement for agent-to-agent commerce.', link: '/agent-onboarding' },
      { name: 'A2A Agent Registry', status: 'Beta', desc: 'Register external agents, earn reputation, take bounties.', link: '/agent-onboarding' },
      { name: 'Agent Work Bounties', status: 'Beta', desc: 'Post or claim commerce tasks with USDC escrow (10% platform fee).' },
      { name: 'Moltbook / OpenClaw', status: 'Beta', desc: 'Agent marketplace bidding and negotiation rails.' },
    ],
  },
  {
    id: 'communication',
    icon: MessageCircle,
    title: 'Communication & Workflow',
    blurb: 'Talk to your business anywhere. Approvals, briefs, and Money Found statements on your channels.',
    items: [
      { name: 'WhatsApp', status: 'Live', desc: 'Primary channel — signup, agent chat, weekly Money Found statements.' },
      { name: 'SMS (Twilio)', status: 'Live', desc: 'Full parity with WhatsApp — reply SMS to switch anytime.' },
      { name: 'Slack', status: 'Live', desc: 'Approvals, margin guard alerts, digests, and reports in your workspace.', link: '/docs' },
      { name: 'Telegram', status: 'Live', desc: 'Deal distribution and agent notifications.' },
      { name: 'Email (SendGrid)', status: 'Live', desc: 'Briefs, invitations, and lifecycle messages.' },
      { name: 'iMessage', status: 'Beta', desc: 'Via the OpenClaw messaging OS.' },
      { name: 'Zapier', status: 'Beta', desc: 'Trigger zaps from commerce events; push actions back in.' },
      { name: 'Intercom', status: 'Beta', desc: 'Support context enriched with commerce health data.' },
    ],
  },
  {
    id: 'context',
    icon: Database,
    title: 'Context & Data',
    blurb: 'Your business memory — portable, queryable, and yours.',
    items: [
      { name: 'Playbooks (Markdown)', status: 'Live', desc: 'Human-editable .md playbooks that constrain every agent — program your OS in plain text.', link: '/playbooks' },
      { name: 'Semantic Memory', status: 'Live', desc: 'Vector + text recall across every conversation and agent decision.' },
      { name: 'Signals API', status: 'Live', desc: 'Market signals and benchmarks as an API ($99/mo add-on).', link: '/signals' },
      { name: 'Public Commerce Index', status: 'Live', desc: 'City and category commerce scores, k-anonymized.', link: '/docs' },
      { name: 'Obsidian vault sync', status: 'Roadmap', desc: 'Pull playbooks and Money Found reports into your personal vault.' },
    ],
  },
  {
    id: 'money',
    icon: Landmark,
    title: 'Payments & Capital',
    blurb: 'Money in, money out, and liquidity when you need it.',
    items: [
      { name: 'Stripe', status: 'Live', desc: 'Subscriptions, trials, billing portal, metered usage.' },
      { name: 'USDC', status: 'Live', desc: 'Agent-to-agent settlement and escrow-protected payouts.' },
      { name: 'Plaid', status: 'Beta', desc: 'Bank connections powering cashflow projection and pre-approved liquidity.' },
      { name: 'thedeals.ai', status: 'Live', desc: 'Consumer deal distribution — 5 deals/mo free, unlimited on Starter.' },
    ],
  },
];

export default function IntegrationsPage() {
  return (
    <div className="min-h-screen bg-[#0B1120] text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
      {/* Nav */}
      <nav className="border-b border-[#22304A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#3EF1C7] flex items-center justify-center">
              <span className="text-[#0B1120] font-bold text-sm">CI</span>
            </div>
            <span className="font-semibold">Commerce Index</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/developers" className="text-sm text-[#94A3B8] hover:text-white">Developers</Link>
            <Link to="/#pricing" className="text-sm text-[#94A3B8] hover:text-white">Pricing</Link>
            <Link to="/" className="px-4 py-2 bg-[#3EF1C7] text-[#0B1120] rounded-lg font-semibold text-sm hover:bg-[#3EF1C7]/90">
              Find my money — free
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <header className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#3EF1C7]/10 border border-[#3EF1C7]/20 mb-6">
          <Plug className="w-4 h-4 text-[#3EF1C7]" />
          <span className="text-sm text-[#3EF1C7] font-medium">Integrations</span>
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold mb-4">
          Plugs Into Your Stack.<br />
          <span className="text-[#3EF1C7]">And Every Other AI's.</span>
        </h1>
        <p className="text-[#94A3B8] max-w-2xl mx-auto">
          Stores, models, agents, and channels — Commerce Index is the commerce layer
          they all talk to. Bring your own LLM keys, your own local models, even your
          own agents.
        </p>
      </header>

      {/* Categories */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 space-y-16">
        {CATEGORIES.map(cat => (
          <section key={cat.id} id={cat.id}>
            <div className="flex items-center gap-3 mb-2">
              <cat.icon className="w-6 h-6 text-[#3EF1C7]" />
              <h2 className="text-2xl font-bold">{cat.title}</h2>
            </div>
            <p className="text-sm text-[#94A3B8] mb-6 max-w-2xl">{cat.blurb}</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {cat.items.map(item => {
                const card = (
                  <div className="h-full p-5 rounded-xl border border-[#22304A] bg-[#0D1628] hover:border-[#3EF1C7]/30 transition-colors">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold">{item.name}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_STYLES[item.status]}`}>
                        {item.status}
                      </span>
                    </div>
                    <p className="text-xs text-[#94A3B8]">{item.desc}</p>
                  </div>
                );
                return item.link
                  ? <Link key={item.name} to={item.link} className="block">{card}</Link>
                  : <div key={item.name}>{card}</div>;
              })}
            </div>
          </section>
        ))}

        {/* CTA */}
        <section className="text-center pt-8 border-t border-[#22304A]">
          <Network className="w-8 h-8 text-[#7A5CFF] mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-3">Missing something?</h2>
          <p className="text-sm text-[#94A3B8] mb-6 max-w-xl mx-auto">
            Building an agent platform, a channel, or a tool merchants use?
            The API is metered, documented, and open for business.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link to="/developers" className="inline-flex items-center gap-2 px-6 py-3 bg-[#3EF1C7] text-[#0B1120] rounded-lg font-semibold hover:bg-[#3EF1C7]/90 transition-colors">
              Build on the API <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3 border border-[#22304A] rounded-lg font-semibold hover:bg-[#1E293B] transition-colors">
              Request an integration
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
}
